'use client'

import { usePathname } from 'next/navigation'
import type React from 'react'
import { OnboardingSurveyGate } from '@/components/onboarding/onboarding-survey-gate'

/**
 * Wraps the dojo app chrome (header / sidebar / footer / cookie banner) and
 * suppresses all of it on the public uninstall-survey and onboarding routes —
 * those render for end-users of third-party extensions (also served via the
 * uninstall-form.extenshi.io / welcome.extenshi.io host rewrites) and must look
 * like a standalone branded page, not the developer cabinet.
 */
export function AppChrome({
	header,
	sidebar,
	footer,
	banner,
	bare = false,
	children,
}: {
	header: React.ReactNode
	sidebar: React.ReactNode
	footer: React.ReactNode
	banner: React.ReactNode
	/**
	 * Server-derived flag (middleware sets x-extenshi-bare for the survey
	 * routes, incl. the uninstall-form.extenshi.io host rewrite, where
	 * usePathname() still reports the original /{id}/{slug} browser path).
	 */
	bare?: boolean
	children: React.ReactNode
}) {
	const pathname = usePathname() ?? ''

	// `/pay` = the buyer's return page after hosted checkout (extenshi-pay): an
	// end-user of a third-party extension, not a dojo account — no chrome.
	if (
		bare ||
		pathname.startsWith('/uninstall-form') ||
		pathname.startsWith('/welcome') ||
		pathname.startsWith('/pay/')
	) {
		return <>{children}</>
	}

	return (
		<>
			<div className="dojo-cabinet min-h-screen flex flex-col">
				{header}
				<div className="flex-1 flex">
					{sidebar}
					{/* overflow-x-clip is a page-level backstop so no block can force a
					    horizontal scroll on small screens; nested overflow-x-auto regions
					    (code snippets, wide tables) keep their own scroll. */}
					<main className="flex-1 min-w-0 overflow-x-clip">{children}</main>
				</div>
				{footer}
			</div>
			{banner}
			<OnboardingSurveyGate />
		</>
	)
}
